import { useEffect } from 'react';

/**
 * Make the phone's Back button dismiss an overlay instead of leaving the app.
 *
 * There is no router here, and there should not be: the sheets are panels over
 * the home screen, not places, and giving them addresses would mean a customer
 * could land on a settings URL with no box loaded behind it. But with no
 * history of its own, the app has exactly one entry, so Back from an open sheet
 * is Back out of the app altogether -- which on an installed PWA closes it.
 *
 * So the overlay gets a history entry and nothing else does. Opening one pushes
 * a state; Back pops it and the overlay closes, which is the gesture everyone
 * already has. Closing it any other way -- the close button, Escape, the scrim
 * -- spends that entry on the way out, or the next Back press would be absorbed
 * silently and the customer would have to press it twice to leave.
 *
 * `isOpen` is deliberately one flag for every overlay rather than one hook per
 * sheet. The account menu opens the box sheet directly, closing itself in the
 * same click: two hooks would pop and push in one tick and race. One flag stays
 * true across that handover, so the entry is pushed once and Back returns to
 * the home screen from wherever the customer ended up.
 *
 * @param {boolean}  isOpen  whether any overlay is currently showing
 * @param {Function} close   stable callback that closes all of them
 */
export const useBackToClose = (isOpen, close) => {
  useEffect(() => {
    if (!isOpen) return undefined;

    window.history.pushState({ overlay: true }, '');

    // Set when Back is what closed it, so the cleanup below knows the entry is
    // already gone and must not pop a second one -- that second pop would come
    // out of the app's own history and leave the page.
    let popped = false;
    const onPop = () => { popped = true; close(); };
    window.addEventListener('popstate', onPop);

    return () => {
      // Removed first. Going back below fires popstate, and by then this
      // overlay is closed and nothing should act on it.
      window.removeEventListener('popstate', onPop);
      if (!popped) window.history.back();
    };
  }, [isOpen, close]);
};

export default useBackToClose;
